import React, { useEffect, useState } from "react";
import PerformanceFilters from "./PerformanceFilters";
import PerformanceSummaryCards from "./PerformanceSummaryCards";
import PerformanceCharts from "./PerformanceCharts";
import TestPerformanceChart from "./TestPerformanceChart";
import PerformanceReviewPanels from "./PerformanceReviewPanels";
import { getFilterOptions, getPerformanceData } from "../pages/services/performanceService";
import "../styles/Dashboard.css";
import "../styles/PerformanceTracking.css";

const istDateString = (date) => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(date);

const defaultRange = () => {
  const to = new Date();
  const from = new Date(to.getTime() - 29 * 24 * 60 * 60 * 1000);
  return { from: istDateString(from), to: istDateString(to) };
};

const EMPTY_OPTIONS = { courses: [], batches: [], students: [], subjects: [] };

const PerformanceTracking = () => {
  const [filters, setFilters] = useState(() => ({
    courseId: "", batchId: "", studentId: "", subjectId: "", ...defaultRange(),
  }));

  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState(null);

  const [perfData, setPerfData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "courseId") {
        next.batchId = "";
        next.studentId = "";
        next.subjectId = "";
      } else if (key === "batchId") {
        next.studentId = "";
      }
      return next;
    });
  };

  // Cascading dropdown options — refetch whenever the parent filters (Course/Batch)
  // change; syncs the Course field when a Batch is picked directly.
  useEffect(() => {
    let cancelled = false;
    setOptionsLoading(true);
    setOptionsError(null);
    getFilterOptions(filters.courseId || undefined, filters.batchId || undefined)
      .then((data) => {
        if (cancelled) return;
        setOptions({ courses: data.courses, batches: data.batches, students: data.students, subjects: data.subjects });
        if (data.impliedCourseId && !filters.courseId) {
          setFilters((prev) => ({ ...prev, courseId: data.impliedCourseId }));
        }
      })
      .catch((err) => !cancelled && setOptionsError(err.message))
      .finally(() => !cancelled && setOptionsLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.courseId, filters.batchId]);

  // Performance data — refetch whenever any filter (including date range) changes.
  useEffect(() => {
    let cancelled = false;
    setDataLoading(true);
    setDataError(null);
    getPerformanceData(filters)
      .then((data) => !cancelled && setPerfData(data))
      .catch((err) => !cancelled && setDataError(err.message))
      .finally(() => !cancelled && setDataLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.courseId, filters.batchId, filters.studentId, filters.subjectId, filters.from, filters.to]);

  return (
    <div className="lp-page dash-page">
      <div className="lp-header">
        <div className="lp-header-left">
          <h1 className="lp-title">Performance Tracking</h1>
        </div>
      </div>

      <div className="dash-card perf-filters-card">
        {optionsError ? (
          <div className="dash-section-error">Couldn't load filter options: {optionsError}</div>
        ) : (
          <PerformanceFilters filters={filters} options={options} onFilterChange={handleFilterChange} />
        )}
        {optionsLoading && <div className="perf-filters-loading">Updating filter options…</div>}
      </div>

      <PerformanceSummaryCards summary={perfData?.summary} loading={dataLoading} error={dataError} />

      <section className="dash-section">
        <div className="dash-card">
          <PerformanceCharts
            hoursTrend={perfData?.hoursTrend}
            questionsTrend={perfData?.questionsTrend}
            notesBySubjectTrend={perfData?.notesBySubjectTrend}
            subjectSeries={perfData?.meta?.subjectSeries}
            loading={dataLoading}
            error={dataError}
          />
        </div>
      </section>

      {/* The Trainer app's Performance Review, recomputed here so the Admin
          sees both views on one screen: app usage above, teaching metrics
          below. The Trainer panel itself is unchanged. */}
      <section className="dash-section">
        <div className="dash-card">
          <h3 className="dash-chart-panel-title">Performance Review</h3>
          <p className="pr-card-sub">
            The same practice, study, consistency, topic-strength and difficulty
            metrics a trainer sees — scoped by the filters above.
          </p>
          <PerformanceReviewPanels filters={filters} />
        </div>
      </section>

      {/* Test performance — scores per test, one line per student. Driven by
          the same batch/student/date filters as the panels above. */}
      <section className="dash-section">
        <div className="dash-card">
          <TestPerformanceChart
            filters={{
              batchId: filters.batchId,
              studentId: filters.studentId,
              from: filters.from,
              to: filters.to,
            }}
          />
        </div>
      </section>
    </div>
  );
};

export default PerformanceTracking;
